
import React, { useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const ClaimCompany = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    fullName: '',
    email: user?.email || '',
    jobTitle: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.fullName || !formData.email || !formData.jobTitle || !formData.message) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const { error } = await supabase
        .from('company_claim_requests' as any)
        .insert({
          company_name: formData.companyName,
          full_name: formData.fullName,
          email: formData.email,
          job_title: formData.jobTitle,
          message: formData.message,
          user_id: user?.id
        });
      
      if (error) throw error;
      
      toast({
        title: "Request submitted",
        description: "Your request to claim this company has been submitted. An administrator will review it shortly.",
      });
      
      setFormData({
        companyName: '',
        fullName: '',
        email: user?.email || '',
        jobTitle: '',
        message: '',
      });
    } catch (error) {
      console.error("Error submitting claim request:", error);
      toast({
        title: "Request failed",
        description: "There was a problem submitting your request. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout 
      title="Claim Company Profile" 
      description="Request to manage your company's profile on RedFlags by submitting the form below."
    >
      <div className="bg-white rounded-lg shadow-sm p-6 max-w-2xl mx-auto border border-redflags-light/20">
        <div className="space-y-6">
          <div className="space-y-3">
            <h3 className="text-xl font-medium text-redflags-dark">
              Claim Your Company Profile
            </h3>
            
            <p className="text-redflags-gray">
              Are you a representative of a company listed on RedFlags? Request management access to your company profile by sending a message to our administrators.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="companyName" className="text-sm font-medium text-gray-700">Company Name</label>
                <Input
                  id="companyName"
                  name="companyName"
                  placeholder="Enter your company name"
                  value={formData.companyName}
                  onChange={handleChange}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="fullName" className="text-sm font-medium text-gray-700">Full Name</label>
                  <Input
                    id="fullName"
                    name="fullName"
                    placeholder="Your full name"
                    value={formData.fullName}
                    onChange={handleChange}
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-gray-700">Email</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Your work email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="jobTitle" className="text-sm font-medium text-gray-700">Job Title</label>
                <Input
                  id="jobTitle"
                  name="jobTitle"
                  placeholder="Your position at the company"
                  value={formData.jobTitle}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium text-gray-700">Message to Administrators</label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Please explain why you should be granted management access to this company profile..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
            </div>
            
            <Button
              type="submit"
              className="bg-redflags-red hover:bg-redflags-red/90 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Request
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};

export default ClaimCompany;
